import { useCallback, useRef, useState } from 'react';
import SignaturePad from 'signature_pad';
import type { UseSignaturePadResult } from './types';

export function useSignaturePad({ src }: { src: string }): UseSignaturePadResult {
  const [isLongImage, setIsLongImage] = useState(false);
  const imgRef = useRef<HTMLImageElement>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);

  const drawImageToCanvas = (
    img: HTMLImageElement,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D | null,
    maxWidth: number,
    maxHeight: number
  ): void => {
    const isLong = img.height > img.width * 1.5;
    setIsLongImage(isLong);
    let drawWidth;
    let drawHeight;
    let scale;
    if (isLong) {
      const maxLongWidth = 600;
      scale = Math.min(maxLongWidth / img.width, 1);
      drawWidth = img.width * scale;
      drawHeight = img.height * scale;
    } else {
      scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
      drawWidth = img.width * scale;
      drawHeight = img.height * scale;
    }
    canvas.width = drawWidth;
    canvas.height = drawHeight;
    canvas.style.width = `${drawWidth}px`;
    canvas.style.height = `${drawHeight}px`;
    ctx?.drawImage(img, 0, 0, drawWidth, drawHeight);
  };

  const initSignaturePad = useCallback((): void => {
    if (canvasRef.current != null) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const maxWidth = window.innerWidth * 0.8;
      const maxHeight = window.innerHeight * 0.8;
      if (ctx != null) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      if (signaturePadRef.current != null) {
        signaturePadRef.current.off();
        signaturePadRef.current = null;
      }
      signaturePadRef.current = new SignaturePad(canvas, {
        penColor: 'red'
      });
      if (imgRef.current != null) {
        drawImageToCanvas(imgRef.current, canvas, ctx, maxWidth, maxHeight);
        return;
      }
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.src = src;
      img.onload = (): void => {
        imgRef.current = img;
        drawImageToCanvas(img, canvas, ctx, maxWidth, maxHeight);
      };
    }
  }, [src]);

  return {
    canvasRef,
    signaturePadRef,
    imgRef,
    isLongImage,
    initSignaturePad
  };
}
